import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Shield, ArrowLeft, Calendar, Clock, User, Tag } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BlogPost = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBlogPost = async () => {
      try {
        const response = await axios.get(`${API}/blog/${id}`);
        setPost(response.data);
      } catch (error) {
        console.error('Error fetching blog post:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchBlogPost();
  }, [id]);

  // Add inline styles for blog content
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .blog-content * {
        color: #d1d5db !important;
      }
      .blog-content h2 {
        color: #22d3ee !important;
        font-size: 1.875rem !important;
        font-weight: bold !important;
        margin-top: 2rem !important;
        margin-bottom: 1rem !important;
      }
      .blog-content h3 {
        color: #67e8f9 !important;
        font-size: 1.5rem !important;
        font-weight: bold !important;
        margin-top: 1.5rem !important;
        margin-bottom: 0.75rem !important;
      }
      .blog-content p {
        color: #d1d5db !important;
        margin-bottom: 1rem !important;
        line-height: 1.8 !important;
      }
      .blog-content ul, .blog-content ol {
        color: #d1d5db !important;
        margin: 1rem 0 !important;
        padding-left: 1.5rem !important;
      }
      .blog-content li {
        color: #d1d5db !important;
        margin: 0.5rem 0 !important;
      }
      .blog-content strong {
        color: #ffffff !important;
        font-weight: 600 !important;
      }
      .blog-content a {
        color: #22d3ee !important;
        text-decoration: underline !important;
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#0a0e1a] via-[#0d1525] to-[#0a0e1a] text-white flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-400 mb-4"></div>
          <p className="text-gray-400">Loading article...</p>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#0a0e1a] via-[#0d1525] to-[#0a0e1a] text-white flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-white mb-4">Article not found</h2>
          <Button onClick={() => navigate('/')} className="bg-gradient-to-r from-cyan-500 to-purple-500">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Home
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0a0e1a] via-[#0d1525] to-[#0a0e1a] text-white">
      {/* Header */}
      <nav className="fixed top-0 w-full bg-[#0a0e1a]/95 backdrop-blur-md border-b border-cyan-500/10 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center space-x-3 cursor-pointer" onClick={() => navigate('/')}>
              <div className="relative">
                <img src="/logo.png" alt="Lexprive Consultants" className="h-14 w-14 object-contain" />
                <div className="absolute inset-0 blur-lg bg-cyan-400/20 -z-10"></div>
              </div>
              <div>
                <span className="text-xl font-bold bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent">
                  Lexprive Consultants LLP
                </span>
                <p className="text-xs text-gray-400">Securing Your Digital Future</p>
              </div>
            </div>
            
            <Button 
              onClick={() => navigate('/')}
              className="bg-gradient-to-r from-cyan-500 to-purple-500 hover:from-cyan-600 hover:to-purple-600 text-white border-0 shadow-lg shadow-cyan-500/50"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Home
            </Button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Article Header */}
          <div className="mb-8">
            <Badge className="mb-4 bg-gradient-to-r from-purple-500/20 to-cyan-500/20 text-purple-400 border-purple-500/30">
              {post.category}
            </Badge>
            
            <h1 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-white via-cyan-200 to-white bg-clip-text text-transparent leading-tight">
              {post.title}
            </h1>

            <div className="flex flex-wrap items-center gap-6 text-gray-400 text-sm mb-6">
              <div className="flex items-center gap-2">
                <User className="h-4 w-4 text-cyan-400" />
                <span>{post.author}</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-cyan-400" />
                <span>{new Date(post.date).toLocaleDateString('en-IN', { 
                  day: 'numeric', 
                  month: 'long', 
                  year: 'numeric' 
                })}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-cyan-400" />
                <span>{post.readTime}</span>
              </div>
            </div>

            <p className="text-xl text-gray-300 leading-relaxed">
              {post.excerpt}
            </p>
          </div>

          {/* Article Content */}
          <Card className="bg-gradient-to-br from-[#1a1f35] to-[#0d1525] border-cyan-500/20 overflow-hidden">
            <CardContent className="p-8 md:p-12">
              <div 
                className="blog-content text-gray-300 leading-relaxed space-y-4"
                dangerouslySetInnerHTML={{ __html: post.content }}
                style={{ 
                  color: '#d1d5db',
                  fontSize: '1.125rem',
                  lineHeight: '1.8'
                }}
              />
            </CardContent>
          </Card>

          {/* Back to Resources Button */}
          <div className="mt-12 text-center">
            <Button 
              onClick={() => navigate('/#resources')}
              size="lg"
              className="bg-gradient-to-r from-cyan-500 to-purple-500 hover:from-cyan-600 hover:to-purple-600 text-white"
            >
              <ArrowLeft className="mr-2 h-5 w-5" />
              Back to All Resources
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogPost;
